import { useTranslation } from 'react-i18next';

import type { FeedbackMessage } from '@/entities/feedback';
import { Avatar } from '@/shared/ui';
import { formatDateTime } from '@/shared/lib/datetime';

import { AttachmentList } from './AttachmentList';
import styles from './Conversation.module.css';

interface Props {
  messages: readonly FeedbackMessage[];
  /** Moderators see the person behind a team reply and the internal notes. */
  staff: boolean;
  /** Replies written by the author: their display name. */
  authorName: string;
  timeZone?: string | undefined;
}

/**
 * The public conversation. Team replies are visually distinct and carry the team name (never a
 * person) for the author; internal notes, which only moderators ever receive, are marked as such.
 * Text is rendered as plain text with line breaks: no HTML, no Markdown.
 */
export function Conversation({ messages, staff, authorName, timeZone }: Props) {
  const { t, i18n } = useTranslation();
  if (messages.length === 0) {
    return <p className={styles.empty}>{t('feedback.conversation.empty')}</p>;
  }
  return (
    <ol className={styles.list} aria-label={t('feedback.conversation.title')}>
      {messages.map((message) => {
        const internal = message.visibility === 'internal';
        const name = message.from_team
          ? staff && message.author
            ? `${t('feedback.team')} · ${message.author.display_name}`
            : t('feedback.team')
          : (message.author?.display_name ?? authorName);
        return (
          <li
            key={message.id}
            className={`${styles.message} ${message.from_team ? styles.team : styles.user} ${internal ? styles.internal : ''}`}
            data-testid={internal ? 'feedback-internal-note' : 'feedback-message'}
          >
            <div className={styles.head}>
              <Avatar
                id={message.author?.id ?? (message.from_team ? 'team' : authorName)}
                name={message.from_team ? t('feedback.team') : name}
                size="sm"
              />
              <span className={styles.name}>{name}</span>
              {internal && (
                <span className={styles.badge}>{t('feedback.conversation.internal')}</span>
              )}
              <time className={styles.when} dateTime={message.created_at}>
                {formatDateTime(message.created_at, i18n.language, timeZone)}
              </time>
            </div>
            <p className={styles.text}>{message.content}</p>
            <AttachmentList attachments={message.attachments} />
          </li>
        );
      })}
    </ol>
  );
}
